import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Search,
  CalendarDays,
  Wallet,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  Download,
  ArrowUpRight,
  ArrowDownRight,
  MoreHorizontal,
  Filter,
  MapPin,
  Users,
  Receipt,
  TrendingUp,
} from "lucide-react";

import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Money, Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/admin/bookings")({
  head: () => ({
    meta: [
      { title: "Bookings — Spayzo Admin" },
      { name: "description", content: "Monitor bookings across all merchants and spaces." },
      { property: "og:title", content: "Bookings — Spayzo Admin" },
      { property: "og:description", content: "Monitor bookings across all merchants and spaces." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

type Status = "pending" | "confirmed" | "completed" | "cancelled" | "refunded";
type Payment = "paid" | "unpaid" | "refunded" | "pending";

type Booking = {
  id: string;
  customer: { name: string; email: string };
  merchant: string;
  space: string;
  city: string;
  date: string;
  start: string;
  end: string;
  guests: number;
  total: number;
  commission: number;
  status: Status;
  payment: Payment;
};

const CITIES = [
  "All cities",
  "Riyadh",
  "Jeddah",
  "Mecca",
  "Medina",
  "Dammam",
  "Khobar",
  "Dhahran",
  "Taif",
  "Tabuk",
  "Abha",
] as const;

const BOOKINGS: Booking[] = [
  {
    id: "BK-20481",
    customer: { name: "Sara Al Otaibi", email: "sara@zephyr.sa" },
    merchant: "The Workspace Hub",
    space: "Executive Suite",
    city: "Riyadh",
    date: "Jul 20, 2026",
    start: "09:00",
    end: "13:00",
    guests: 6,
    total: 1240,
    commission: 186,
    status: "confirmed",
    payment: "paid",
  },
  {
    id: "BK-20480",
    customer: { name: "Faisal Nazer", email: "faisal@haus.co" },
    merchant: "Loftworks Jeddah",
    space: "Meeting Loft",
    city: "Jeddah",
    date: "Jul 20, 2026",
    start: "14:00",
    end: "17:00",
    guests: 8,
    total: 890,
    commission: 133,
    status: "confirmed",
    payment: "pending",
  },
  {
    id: "BK-20479",
    customer: { name: "Nora Al Harbi", email: "nora@pixel.sa" },
    merchant: "Studio Central",
    space: "Photo Bay",
    city: "Riyadh",
    date: "Jul 19, 2026",
    start: "10:00",
    end: "12:00",
    guests: 3,
    total: 520,
    commission: 78,
    status: "completed",
    payment: "paid",
  },
  {
    id: "BK-20478",
    customer: { name: "Omar Al Rashid", email: "omar@build.io" },
    merchant: "Dammam Deskspace",
    space: "Pod Room",
    city: "Dammam",
    date: "Jul 19, 2026",
    start: "13:00",
    end: "18:00",
    guests: 2,
    total: 340,
    commission: 51,
    status: "cancelled",
    payment: "refunded",
  },
  {
    id: "BK-20477",
    customer: { name: "Layla Al Zahrani", email: "layla@arch.sa" },
    merchant: "Mecca Co-Working",
    space: "Board Room",
    city: "Mecca",
    date: "Jul 18, 2026",
    start: "09:30",
    end: "11:30",
    guests: 10,
    total: 1580,
    commission: 237,
    status: "completed",
    payment: "paid",
  },
  {
    id: "BK-20476",
    customer: { name: "Yousef Nabil", email: "yousef@sable.co" },
    merchant: "The Workspace Hub",
    space: "Rooftop Lounge",
    city: "Riyadh",
    date: "Jul 18, 2026",
    start: "17:00",
    end: "22:00",
    guests: 25,
    total: 4200,
    commission: 630,
    status: "confirmed",
    payment: "paid",
  },
  {
    id: "BK-20475",
    customer: { name: "Hana Al Qassim", email: "hana@lume.sa" },
    merchant: "Loftworks Jeddah",
    space: "Creative Suite",
    city: "Jeddah",
    date: "Jul 17, 2026",
    start: "10:00",
    end: "16:00",
    guests: 5,
    total: 1120,
    commission: 168,
    status: "refunded",
    payment: "refunded",
  },
  {
    id: "BK-20474",
    customer: { name: "Khaled Barakah", email: "khaled@monkfish.io" },
    merchant: "Medina Hub",
    space: "Focus Room",
    city: "Medina",
    date: "Jul 17, 2026",
    start: "08:00",
    end: "12:00",
    guests: 1,
    total: 260,
    commission: 39,
    status: "completed",
    payment: "paid",
  },
  {
    id: "BK-20473",
    customer: { name: "Reem Al Dosari", email: "reem@joro.co" },
    merchant: "Abha Retreat",
    space: "Garden Deck",
    city: "Abha",
    date: "Jul 16, 2026",
    start: "15:00",
    end: "19:00",
    guests: 12,
    total: 1980,
    commission: 297,
    status: "confirmed",
    payment: "paid",
  },
  {
    id: "BK-20472",
    customer: { name: "Tariq Al Amri", email: "tariq@fenix.sa" },
    merchant: "Tabuk Studios",
    space: "Recording Booth",
    city: "Tabuk",
    date: "Jul 16, 2026",
    start: "11:00",
    end: "14:00",
    guests: 2,
    total: 460,
    commission: 69,
    status: "confirmed",
    payment: "unpaid",
  },
];

const STATUS_STYLES: Record<Status, string> = {
  confirmed: "bg-primary/10 text-primary border-primary/20",
  pending: "bg-amber-500/10 text-amber-700 border-amber-500/20",
  completed: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
  cancelled: "bg-rose-500/10 text-rose-700 border-rose-500/20",
  refunded: "bg-sky-500/10 text-sky-700 border-sky-500/20",
};

const STATUS_DOT: Record<Status, string> = {
  confirmed: "bg-primary",
  pending: "bg-amber-500",
  completed: "bg-emerald-500",
  cancelled: "bg-rose-500",
  refunded: "bg-sky-500",
};

const PAYMENT_STYLES: Record<Payment, string> = {
  paid: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
  unpaid: "bg-rose-500/10 text-rose-700 border-rose-500/20",
  refunded: "bg-sky-500/10 text-sky-700 border-sky-500/20",
  pending: "bg-amber-500/10 text-amber-700 border-amber-500/20",
};

const BRAND = "#5958F3";

const KPI_DEFS = [
  { key: "totalBookings", value: "1,428", delta: "+12.4%", up: true, icon: CalendarDays, tint: "bg-[#5958F3]/10 text-[#5958F3]", bar: "bg-[#5958F3]" },
  { key: "grossRevenue", value: <Money value="428.6K" />, delta: "+8.1%", up: true, icon: Wallet, tint: "bg-emerald-500/10 text-emerald-700", bar: "bg-emerald-500" },
  { key: "confirmed", value: "1,092", delta: "+6.3%", up: true, icon: CheckCircle2, tint: "bg-sky-500/10 text-sky-700", bar: "bg-sky-500" },
  { key: "cancellations", value: "84", delta: "-2.1%", up: false, icon: XCircle, tint: "bg-rose-500/10 text-rose-700", bar: "bg-rose-500" },
] as const;

const STATUS_TAB_KEYS: Array<Status | "all"> = ["all", "confirmed", "completed", "cancelled", "refunded"];

function initials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function Page() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<Status | "all">("all");
  const [city, setCity] = useState<(typeof CITIES)[number]>("All cities");
  const [payment, setPayment] = useState<Payment | "all">("all");
  const [detail, setDetail] = useState<Booking | null>(null);

  const rows = useMemo(() => {
    return BOOKINGS.filter((b) => {
      const qq = q.trim().toLowerCase();
      const match =
        !qq ||
        b.id.toLowerCase().includes(qq) ||
        b.customer.name.toLowerCase().includes(qq) ||
        b.customer.email.toLowerCase().includes(qq) ||
        b.merchant.toLowerCase().includes(qq) ||
        b.space.toLowerCase().includes(qq);
      const s = status === "all" || b.status === status;
      const c = city === "All cities" || b.city === city;
      const p = payment === "all" || b.payment === payment;
      return match && s && c && p;
    });
  }, [q, status, city, payment]);

  const counts = useMemo(() => {
    const base: Record<Status | "all", number> = {
      all: BOOKINGS.length,
      confirmed: 0,
      pending: 0,
      completed: 0,
      cancelled: 0,
      refunded: 0,
    };
    for (const b of BOOKINGS) base[b.status]++;
    return base;
  }, []);

  const activeFilters = [
    status !== "all" && t("pages.admin.bookings.filters.statusChip", { value: t(`pages.admin.bookings.status.${status}`) }),
    city !== "All cities" && t("pages.admin.bookings.filters.cityChip", { value: city }),
    payment !== "all" && t("pages.admin.bookings.filters.paymentChip", { value: t(`pages.admin.bookings.payment.${payment}`) }),
    q.trim() && t("pages.admin.bookings.filters.searchChip", { value: q.trim() }),
  ].filter(Boolean) as string[];

  const clearFilters = () => {
    setQ("");
    setStatus("all");
    setCity("All cities");
    setPayment("all");
  };

  return (
    <AdminShell
      title={t("pages.admin.bookings.title")}
      subtitle={t("pages.admin.bookings.subtitle")}
      actions={
        <>
          <Button variant="outline" className="h-10 rounded-xl">
            <Filter className="h-4 w-4" /> {t("pages.admin.bookings.segments")}
          </Button>
          <Button className="h-10 rounded-xl text-white" style={{ backgroundColor: BRAND }}>
            <Download className="h-4 w-4" /> {t("pages.admin.bookings.exportCsv")}
          </Button>
        </>
      }
    >
      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {KPI_DEFS.map((k) => (
          <Card
            key={k.key}
            className="group relative overflow-hidden rounded-2xl border-border/60 p-5 shadow-soft transition hover:-translate-y-0.5 hover:shadow-md"
          >
            <span className={cn("absolute inset-x-0 top-0 h-1", k.bar)} />
            <div className="flex items-start justify-between">
              <span className={cn("grid h-11 w-11 place-items-center rounded-xl", k.tint)}>
                <k.icon className="h-5 w-5" />
              </span>
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
                  k.up ? "bg-emerald-500/10 text-emerald-700" : "bg-rose-500/10 text-rose-700",
                )}
              >
                {k.up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                {k.delta}
              </span>
            </div>
            <p className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t(`pages.admin.bookings.kpis.${k.key}`)}</p>
            <p className="mt-1 font-display text-2xl font-bold tracking-tight">{k.value}</p>
            <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
              <TrendingUp className="h-3 w-3" /> {t("pages.admin.bookings.kpis.vsLast30")}
            </p>
          </Card>
        ))}
      </div>

      {/* Status tabs */}
      <div className="mt-6">
        <Tabs value={status} onValueChange={(v) => setStatus(v as Status | "all")}>
          <TabsList className="h-11 w-full justify-start gap-1 overflow-x-auto rounded-xl bg-surface p-1">
            {STATUS_TAB_KEYS.map((tabKey) => (
              <TabsTrigger
                key={tabKey}
                value={tabKey}
                className="rounded-lg px-3 text-xs font-semibold data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-soft"
              >
                <span
                  className={cn(
                    "me-2 h-1.5 w-1.5 rounded-full",
                    tabKey === "all" ? "bg-muted-foreground/50" : STATUS_DOT[tabKey as Status],
                  )}
                />
                {t(`pages.admin.bookings.status.${tabKey}`)}
                <span className="ms-2 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                  {counts[tabKey]}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>


      {/* Filters */}
      <Card className="mt-4 rounded-2xl border-border/60 p-4 shadow-soft">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[260px] flex-1">
            <Search className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("pages.admin.bookings.filters.searchPlaceholder")}
              className="h-10 rounded-xl border-border/70 bg-surface ps-10"
            />
          </div>
          <Select value={payment} onValueChange={(v) => setPayment(v as Payment | "all")}>
            <SelectTrigger className="h-10 w-[150px] rounded-xl">
              <SelectValue placeholder={t("pages.admin.bookings.payment.placeholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("pages.admin.bookings.payment.all")}</SelectItem>
              <SelectItem value="paid">{t("pages.admin.bookings.payment.paid")}</SelectItem>
              <SelectItem value="unpaid">{t("pages.admin.bookings.payment.unpaid")}</SelectItem>
              <SelectItem value="pending">{t("pages.admin.bookings.payment.pending")}</SelectItem>
              <SelectItem value="refunded">{t("pages.admin.bookings.payment.refunded")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={city} onValueChange={(v) => setCity(v as (typeof CITIES)[number])}>
            <SelectTrigger className="h-10 w-[150px] rounded-xl">
              <SelectValue placeholder={t("pages.admin.bookings.cities.placeholder")} />
            </SelectTrigger>
            <SelectContent>
              {CITIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c === "All cities" ? t("pages.admin.bookings.cities.all") : c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {activeFilters.length > 0 && (
            <Button variant="ghost" className="h-10 rounded-xl text-muted-foreground" onClick={clearFilters}>
              {t("pages.admin.bookings.filters.clearAll")}
            </Button>
          )}
        </div>
        {activeFilters.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {activeFilters.map((f) => (
              <Badge
                key={f}
                variant="outline"
                className="rounded-full border-border/60 bg-surface text-[11px] font-medium text-muted-foreground"
              >
                {f}
              </Badge>
            ))}
          </div>
        )}
      </Card>

      {/* Table */}
      <Card className="mt-6 overflow-hidden rounded-2xl border-border/60 shadow-soft">
        <div className="flex items-center justify-between border-b border-border/60 p-5">
          <div>
            <p className="font-display text-lg font-bold">{t("pages.admin.bookings.table.allBookings")}</p>
            <p className="text-xs text-muted-foreground">
              {t("pages.admin.bookings.table.showing")}{" "}
              <span className="font-semibold text-foreground">{rows.length}</span>{" "}
              {t("pages.admin.bookings.table.of")} {BOOKINGS.length} {t("pages.admin.bookings.table.bookings")}
            </p>
          </div>
          <Badge
            variant="outline"
            className="rounded-full border-[#5958F3]/30 bg-[#5958F3]/10 text-[10px] font-semibold text-[#5958F3]"
          >
            <span className="me-1 h-1.5 w-1.5 animate-pulse rounded-full bg-[#5958F3]" /> {t("pages.admin.bookings.table.live")}
          </Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-5 py-3 text-start font-semibold">{t("pages.admin.bookings.table.cols.booking")}</th>
                <th className="px-3 py-3 text-start font-semibold">{t("pages.admin.bookings.table.cols.customer")}</th>
                <th className="px-3 py-3 text-start font-semibold">{t("pages.admin.bookings.table.cols.merchantSpace")}</th>
                <th className="px-3 py-3 text-start font-semibold">{t("pages.admin.bookings.table.cols.dateTime")}</th>
                <th className="px-3 py-3 text-end font-semibold">{t("pages.admin.bookings.table.cols.total")}</th>
                <th className="px-3 py-3 text-end font-semibold">{t("pages.admin.bookings.table.cols.commission")}</th>
                <th className="px-3 py-3 text-start font-semibold">{t("pages.admin.bookings.table.cols.status")}</th>
                <th className="px-3 py-3 text-start font-semibold">{t("pages.admin.bookings.table.cols.payment")}</th>
                <th className="px-5 py-3 text-end font-semibold">{t("pages.admin.bookings.table.cols.action")}</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className="border-t border-border/60 transition hover:bg-surface/70">
                  <td className="px-5 py-3.5">
                    <p className="font-semibold">{b.id}</p>
                    <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                      <Users className="h-3 w-3" /> {t("pages.admin.bookings.table.guests", { count: b.guests })}
                    </p>
                  </td>
                  <td className="px-3 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <span className="grid h-8 w-8 place-items-center rounded-full bg-[#5958F3]/10 text-[10px] font-bold text-[#5958F3]">
                        {initials(b.customer.name)}
                      </span>
                      <div>
                        <p className="font-medium">{b.customer.name}</p>
                        <p className="text-[11px] text-muted-foreground">{b.customer.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3.5">
                    <p className="font-medium">{b.merchant}</p>
                    <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                      {b.space} · <MapPin className="h-3 w-3" /> {b.city}
                    </p>
                  </td>
                  <td className="px-3 py-3.5">
                    <p className="font-medium">{b.date}</p>
                    <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {b.start} – {b.end}
                    </p>
                  </td>
                  <td className="px-3 py-3.5 text-end font-semibold tabular-nums"><Riyal /> {b.total.toLocaleString()}</td>
                  <td className="px-3 py-3.5 text-end tabular-nums text-muted-foreground"><Riyal /> {b.commission.toLocaleString()}
                  </td>
                  <td className="px-3 py-3.5">
                    <Badge
                      variant="outline"
                      className={cn("rounded-full text-[10px] font-semibold", STATUS_STYLES[b.status])}
                    >
                      <span className={cn("me-1 h-1.5 w-1.5 rounded-full", STATUS_DOT[b.status])} />
                      {t(`pages.admin.bookings.status.${b.status}`)}
                    </Badge>
                  </td>
                  <td className="px-3 py-3.5">
                    <Badge
                      variant="outline"
                      className={cn("rounded-full text-[10px] font-semibold", PAYMENT_STYLES[b.payment])}
                    >
                      {t(`pages.admin.bookings.payment.${b.payment}`)}
                    </Badge>
                  </td>
                  <td className="px-5 py-3.5 text-end">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" className="h-8 rounded-lg" onClick={() => setDetail(b)}>
                        <Eye className="h-3.5 w-3.5" /> {t("pages.admin.bookings.table.view")}
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg" aria-label={t("pages.admin.bookings.table.moreActions")}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onSelect={() => setDetail(b)}>
                            <Eye className="h-4 w-4" /> {t("pages.admin.bookings.table.viewDetails")}
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Receipt className="h-4 w-4" /> {t("pages.admin.bookings.table.downloadInvoice")}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />

                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-rose-600 focus:text-rose-600">
                            {t("pages.admin.bookings.table.issueRefund")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-5 py-16 text-center">
                    <div className="mx-auto flex max-w-sm flex-col items-center gap-2 text-muted-foreground">
                      <div className="grid h-12 w-12 place-items-center rounded-full bg-surface">
                        <Search className="h-5 w-5" />
                      </div>
                      <p className="text-sm font-medium text-foreground">{t("pages.admin.bookings.table.empty.title")}</p>
                      <p className="text-xs">{t("pages.admin.bookings.table.empty.desc")}</p>
                      <Button variant="outline" size="sm" className="mt-2 rounded-lg" onClick={clearFilters}>
                        {t("pages.admin.bookings.table.empty.clear")}
                      </Button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {rows.length > 0 && (
          <div className="flex items-center justify-between border-t border-border/60 px-5 py-3 text-xs text-muted-foreground">
            <span>
              {t("pages.admin.bookings.table.pagination.showingRange", { count: rows.length, total: BOOKINGS.length })}
            </span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" className="h-8 rounded-lg" disabled>
                {t("pages.admin.bookings.table.pagination.previous")}
              </Button>
              <Button variant="outline" size="sm" className="h-8 rounded-lg" disabled>
                {t("pages.admin.bookings.table.pagination.next")}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg rounded-2xl">
          {detail && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <DialogTitle>{detail.id}</DialogTitle>
                  <Badge
                    variant="outline"
                    className={cn("rounded-full text-[10px] font-semibold", STATUS_STYLES[detail.status])}
                  >
                    <span className={cn("me-1 h-1.5 w-1.5 rounded-full", STATUS_DOT[detail.status])} />
                    {t(`pages.admin.bookings.status.${detail.status}`)}
                  </Badge>
                </div>
                <DialogDescription>
                  {detail.merchant} · {detail.space}
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Field label={t("pages.admin.bookings.dialog.customer")} value={detail.customer.name} sub={detail.customer.email} />
                <Field label={t("pages.admin.bookings.dialog.city")} value={detail.city} />
                <Field label={t("pages.admin.bookings.dialog.date")} value={detail.date} />
                <Field label={t("pages.admin.bookings.dialog.time")} value={`${detail.start} – ${detail.end}`} />
                <Field label={t("pages.admin.bookings.dialog.guests")} value={String(detail.guests)} />
                <Field label={t("pages.admin.bookings.dialog.total")} value={<Money value={detail.total.toLocaleString()} />} />
                <Field label={t("pages.admin.bookings.dialog.commission")} value={<Money value={detail.commission.toLocaleString()} />} />
                <Field
                  label={t("pages.admin.bookings.dialog.payoutToMerchant")}
                  value={<Money value={(detail.total - detail.commission).toLocaleString()} />}
                />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Badge
                  variant="outline"
                  className={cn("rounded-full text-[10px] font-semibold", PAYMENT_STYLES[detail.payment])}
                >
                  {t("pages.admin.bookings.dialog.paymentLabel", { value: t(`pages.admin.bookings.payment.${detail.payment}`) })}
                </Badge>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" className="h-10 rounded-xl">
                  <Download className="h-4 w-4" /> {t("pages.admin.bookings.dialog.downloadInvoice")}
                </Button>
                <Button className="h-10 rounded-xl text-white" style={{ backgroundColor: BRAND }}>
                  {t("pages.admin.bookings.dialog.contactMerchant")}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}


function Field({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-surface p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-semibold">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  );
}
